// src/pages/GeneralRafflePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { raffleService } from '../services/raffleService';
import './GeneralRafflePage.css';

const GeneralRafflePage = ({ user, addNotification }) => {
    const [activeRaffle, setActiveRaffle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [countdown, setCountdown] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        total: 0
    });

    // Load data - SIMPLIFIED
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            // Get active events
            const eventsRes = await raffleService.getActiveEvents();
            console.log('Events response:', eventsRes);
            
            if (eventsRes.success && eventsRes.events && eventsRes.events.length > 0) {
                const now = new Date();
                // Find the first upcoming event
                const upcomingEvent = eventsRes.events.find(e => new Date(e.draw_date) > now);
                
                if (upcomingEvent) {
                    setActiveRaffle(upcomingEvent);
                    console.log('Active raffle set:', upcomingEvent);
                }
            }
            
        } catch (error) {
            console.error('Load data error:', error);
            addNotification('Failed to load raffle data', 'error');
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        loadData();
        // Auto-refresh every 30 seconds
        const interval = setInterval(loadData, 30000);
        return () => clearInterval(interval);
    }, [loadData]);

    // Update countdown timer
    useEffect(() => {
        if (!activeRaffle?.draw_date) return;
        
        const updateCountdown = () => {
            const now = new Date();
            const drawDate = new Date(activeRaffle.draw_date);
            const diff = drawDate - now;
            
            if (diff > 0) {
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                setCountdown({ days, hours, minutes, seconds, total: diff });
            } else {
                setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });
                // Trigger celebration when countdown hits zero
                if (diff <= 0 && diff > -5000) {
                    triggerCelebration();
                }
            }
        };
        
        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [activeRaffle]);

    // Trigger celebration effects
    const triggerCelebration = () => {
        // Confetti explosion
        confetti({
            particleCount: 200,
            spread: 100,
            origin: { y: 0.6 },
            startVelocity: 25,
            colors: ['#00843D', '#FFD700', '#FF6B6B', '#4ECDC4']
        });
        
        setTimeout(() => {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.7, x: 0.3 },
                startVelocity: 20,
            });
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.7, x: 0.7 },
                startVelocity: 20,
            });
        }, 300);
        
        setTimeout(() => {
            confetti({
                particleCount: 300,
                spread: 120,
                origin: { y: 0.5 },
                startVelocity: 30,
            });
        }, 600);
        
        // Reload to get winners
        setTimeout(() => {
            loadData();
        }, 1000);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'TBD';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Animated number component
    const AnimatedNumber = ({ value, label }) => {
        return (
            <motion.div 
                className="countdown-block"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
                <motion.span 
                    className="countdown-number"
                    key={value}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    {value}
                </motion.span>
                <span className="countdown-label">{label}</span>
            </motion.div>
        );
    };

    if (loading) {
        return (
            <div className="general-raffle-loading">
                <div className="spinner"></div>
                <p>Loading raffle draw...</p>
            </div>
        );
    }

    return (
        <div className="general-raffle-page">
            {/* Main Content */}
            <div className="raffle-hero-section">
                <div className="hero-bg-particles"></div>
                <div className="hero-content">
                    <motion.div 
                        className="hero-badge"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <i className="fa-solid fa-gift"></i>
                        <span>RA Club Raffle Draw</span>
                    </motion.div>
                    
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        Next Draw
                    </motion.h1>
                    
                    {activeRaffle && countdown.total > 0 && (
                        <motion.div 
                            className="countdown-container"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <div className="countdown-title">
                                <i className="fa-regular fa-clock"></i>
                                <span>Countdown to Win!</span>
                            </div>
                            <div className="countdown-timer-large">
                                <AnimatedNumber value={countdown.days} label="Days" />
                                <div className="countdown-separator">:</div>
                                <AnimatedNumber value={countdown.hours} label="Hours" />
                                <div className="countdown-separator">:</div>
                                <AnimatedNumber value={countdown.minutes} label="Mins" />
                                <div className="countdown-separator">:</div>
                                <AnimatedNumber value={countdown.seconds} label="Secs" />
                            </div>
                            <div className="event-info">
                                <h2>{activeRaffle.title}</h2>
                                <p className="draw-date">
                                    <i className="fa-regular fa-calendar"></i>
                                    Draw Date: {formatDate(activeRaffle.draw_date)}
                                </p>
                                <p className="prize-count">
                                    <i className="fa-solid fa-gift"></i>
                                    {activeRaffle.total_prizes || 0} Amazing Prizes!
                                </p>
                            </div>
                        </motion.div>
                    )}
                    
                    {(!activeRaffle || countdown.total === 0) && (
                        <motion.div 
                            className="no-event-container"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <i className="fa-solid fa-calendar-day"></i>
                            <h2>No Upcoming Draws</h2>
                            <p>Check back soon for exciting raffle events!</p>
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Particle Animation for Countdown */}
            <AnimatePresence>
                {countdown.total > 0 && countdown.total < 60000 && (
                    <motion.div 
                        className="countdown-particles"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        {[...Array(50)].map((_, i) => (
                            <div 
                                key={i}
                                className="particle"
                                style={{
                                    left: `${Math.random() * 100}%`,
                                    animationDelay: `${Math.random() * 5}s`,
                                    animationDuration: `${2 + Math.random() * 3}s`
                                }}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Decorative Floating Elements */}
            <div className="floating-elements">
                <div className="floating-star star-1">✨</div>
                <div className="floating-star star-2">⭐</div>
                <div className="floating-star star-3">🌟</div>
                <div className="floating-star star-4">✨</div>
                <div className="floating-star star-5">⭐</div>
            </div>
        </div>
    );
};

export default GeneralRafflePage;